function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import cn from 'classnames';
import React, { useState, useEffect, useMemo } from 'react';
import { Typography, Tooltip, NLFField, MenuItem } from 'seagull';
import styles from "./BillingSettingsSection.module.scss";
import { serviceBillingModel, serviceModel } from "../../models";
import { BillingDayOfMonthModel } from "../../models";
import { ordinal } from "../../infra/utils";
import { validateBillingDayOfMonth } from "../../models/service";
import { useStoreActions } from "../../contexts";
import ConditionalWrapper from "../ConditionalWrapper/ConditionalWrapper";
import { daysOfWeek, defaultBillingDayOfWeek } from "../../constants";
import { useFeatureFlags } from "../../hooks/useFeatureFlags";
function getDayOfMonthOptions(isBilledUpfront, NEW_BILLING_DAY) {
  return new Array(NEW_BILLING_DAY ? 28 : 27).fill(null).map((_, idx) => ({
    value: (idx + 1).toString(),
    display: ordinal(idx + 1),
    disabled: false
  })).concat({
    value: 'last',
    display: 'last day',
    disabled: isBilledUpfront
  });
}
function BillingDayOfMonthField(_ref) {
  let {
    service,
    billing,
    dayOfMonthOptions,
    isReadOnly,
    disableBillingDay,
    disabled,
    testid,
    showError,
    isActiveAgreement,
    Text,
    onHasError,
    onDayOfMonthChange,
    setShowBillingDayOfMonthError
  } = _ref;
  const {
    logBI
  } = useStoreActions();
  const isBilledUpfront = serviceModel.isBilledUpfront(service);
  const {
    billingDayOfMonth
  } = billing;
  const value = billingDayOfMonth ? billingDayOfMonth.toNormalizedDayOfMonth().toString() : 'last';
  const readOnlyValue = useMemo(() => {
    var _dayOfMonthOptions$fi;
    return ((_dayOfMonthOptions$fi = dayOfMonthOptions.find(opt => opt.value === value)) === null || _dayOfMonthOptions$fi === void 0 ? void 0 : _dayOfMonthOptions$fi.display) || '';
  }, [dayOfMonthOptions, value]);
  return isReadOnly ? /*#__PURE__*/React.createElement(Text, null, readOnlyValue) : /*#__PURE__*/React.createElement(NLFField, {
    skin: "primary",
    disabled: disableBillingDay || disabled,
    value: value,
    testid: "".concat(testid, "billing-day-dropdown"),
    error: showError,
    onChange: e => {
      const day = e.target.value;
      logBI(11014, {
        isActiveAgreement,
        serviceId: service.id,
        day
      });
      const isLast = day === dayOfMonthOptions[dayOfMonthOptions.length - 1].value;
      const newBillingDayOfMonth = BillingDayOfMonthModel.make(isLast ? 'last' : +day);
      const hasError = validateBillingDayOfMonth(newBillingDayOfMonth, isBilledUpfront);
      setShowBillingDayOfMonthError(hasError);
      onHasError && onHasError(service.id, hasError);
      onDayOfMonthChange(service.id, newBillingDayOfMonth);
    }
  }, dayOfMonthOptions.map((_ref2, idx) => {
    let {
      value,
      disabled,
      display
    } = _ref2;
    return /*#__PURE__*/React.createElement(MenuItem, {
      key: value,
      value: value,
      disabled: disabled,
      "data-testid": "".concat(testid, "billing-day-option-").concat(idx)
    }, display);
  }));
}
function BilledUpfrontField(_ref3) {
  let {
    service,
    isReadOnly,
    disableBillUpfront,
    disabled,
    testid,
    isActiveAgreement,
    Text,
    onHasError,
    onBillUpfrontModeChange,
    setShowBillingDayOfMonthError
  } = _ref3;
  const {
    logBI
  } = useStoreActions();
  const value = serviceModel.isBilledUpfront(service) ? 'true' : 'false';
  const billUpfrontOptions = useMemo(() => [{
    key: 'start',
    value: 'true',
    testid: "".concat(testid, "is-billed-upfront-option-1"),
    text: "upfront".concat(serviceBillingModel.isQuarterly(service.billing) ? ' on the 1st day of every quarter' : '')
  }, {
    key: 'end',
    value: 'false',
    testid: "".concat(testid, "is-billed-upfront-option-0"),
    text: serviceBillingModel.isQuarterly(service.billing) ? 'on the last day of every quarter' : 'at the end of the billing cycle'
  }], [testid, service]);
  const readOnlyValue = useMemo(() => {
    var _billUpfrontOptions$f;
    return ((_billUpfrontOptions$f = billUpfrontOptions.find(opt => opt.value === value)) === null || _billUpfrontOptions$f === void 0 ? void 0 : _billUpfrontOptions$f.text) || '';
  }, [billUpfrontOptions, value]);
  return isReadOnly ? /*#__PURE__*/React.createElement(Text, null, readOnlyValue) : /*#__PURE__*/React.createElement(NLFField, {
    skin: "primary",
    disabled: disableBillUpfront || disabled,
    value: value,
    testid: "".concat(testid, "is-billed-upfront-dropdown"),
    onChange: e => {
      const isBilledUpfront = JSON.parse(e.target.value);
      logBI(11016, {
        isActiveAgreement,
        serviceId: service.id,
        isBilledUpfront
      });
      const billingDayOfMonth = serviceModel.billingDayOfMonth(service);
      const hasError = billingDayOfMonth != null && validateBillingDayOfMonth(billingDayOfMonth, isBilledUpfront);
      setShowBillingDayOfMonthError(hasError);
      onHasError && onHasError(service.id, hasError);
      onBillUpfrontModeChange(service.id, isBilledUpfront);
    }
  }, billUpfrontOptions.map(_ref4 => {
    let {
      key,
      value,
      testid,
      text
    } = _ref4;
    return /*#__PURE__*/React.createElement(MenuItem, {
      key: key,
      value: value,
      "data-testid": testid
    }, text);
  }));
}
function BillingDayOfWeekField(_ref5) {
  let {
    service,
    disableBillingDay,
    disabled,
    testid,
    isActiveAgreement,
    isReadOnly,
    Text,
    onDayOfWeekChange
  } = _ref5;
  const {
    logBI
  } = useStoreActions();
  const value = serviceBillingModel.billingDayOfWeek(service.billing) || defaultBillingDayOfWeek;
  const readOnlyValue = useMemo(() => daysOfWeek.find(day => day === value), [value]);
  return isReadOnly ? /*#__PURE__*/React.createElement(Text, null, readOnlyValue) : /*#__PURE__*/React.createElement(NLFField, {
    skin: "primary",
    disabled: disableBillingDay || disabled,
    value: value,
    testid: "".concat(testid, "billing-day-dropdown"),
    onChange: e => {
      logBI(11015, {
        isActiveAgreement,
        serviceId: service.id,
        day: e.target.value
      });
      onDayOfWeekChange(service.id, e.target.value);
    }
  }, daysOfWeek.map((option, idx) => /*#__PURE__*/React.createElement(MenuItem, {
    key: option,
    value: option,
    "data-testid": "".concat(testid, "billing-day-option-").concat(idx)
  }, option)));
}
function BillingSettingsSection(props) {
  const {
    NEW_BILLING_DAY = false
  } = useFeatureFlags();
  const {
    service,
    testid,
    skin,
    className
  } = props;
  const [showBillingDayOfMonthError, setShowBillingDayOfMonthError] = useState(false);
  const isBilledUpfront = serviceModel.isBilledUpfront(service);
  const billing = serviceModel.billingTrigger(service);
  const dayOfMonthOptions = useMemo(() => getDayOfMonthOptions(isBilledUpfront, NEW_BILLING_DAY), [isBilledUpfront, NEW_BILLING_DAY]);
  const Text = props => /*#__PURE__*/React.createElement(Typography, _extends({
    noWrap: true,
    skin: skin
  }, props));
  useEffect(() => {
    setShowBillingDayOfMonthError(serviceModel.isMonthly(service) && validateBillingDayOfMonth(serviceModel.billingDayOfMonth(service), serviceModel.isBilledUpfront(service)));
  }, [service]);
  const renderRepeatableManualSettings = billing => serviceBillingModel.isYearly(billing) || serviceBillingModel.isQuarterly(billing) ? null : /*#__PURE__*/React.createElement("div", {
    className: cn(styles.root, className)
  }, /*#__PURE__*/React.createElement(Text, {
    "data-testid": "".concat(testid, "billing-period-label")
  }, "Billing period ".concat(serviceModel.isBilledUpfront(service) ? 'starts' : 'ends', " on")), "\xA0", serviceBillingModel.isMonthly(billing) ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, null, "the"), "\xA0", /*#__PURE__*/React.createElement(BillingDayOfMonthField, _extends({}, props, {
    billing: billing,
    showError: showBillingDayOfMonthError,
    dayOfMonthOptions: dayOfMonthOptions,
    setShowBillingDayOfMonthError: setShowBillingDayOfMonthError,
    Text: Text
  })), "\xA0", /*#__PURE__*/React.createElement(Text, null, "of each month")) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(BillingDayOfWeekField, _extends({}, props, {
    Text: Text
  })), "\xA0", /*#__PURE__*/React.createElement(Text, null, "of ", serviceBillingModel.isWeekly(billing) ? 'each week' : 'every other week')));
  const renderOngoingBillingSettings = billing => /*#__PURE__*/React.createElement("div", {
    className: cn(styles.root, className)
  }, /*#__PURE__*/React.createElement(Text, null, "Billing is done"), "\xA0", /*#__PURE__*/React.createElement(BilledUpfrontField, _extends({}, props, {
    setShowBillingDayOfMonthError: setShowBillingDayOfMonthError,
    Text: Text
  })), serviceBillingModel.isMonthly(billing) ? /*#__PURE__*/React.createElement(React.Fragment, null, "\xA0", /*#__PURE__*/React.createElement(Text, null, "on the"), "\xA0", /*#__PURE__*/React.createElement(BillingDayOfMonthField, _extends({}, props, {
    billing: billing,
    showError: showBillingDayOfMonthError,
    dayOfMonthOptions: dayOfMonthOptions,
    setShowBillingDayOfMonthError: setShowBillingDayOfMonthError,
    Text: Text
  })), "\xA0", /*#__PURE__*/React.createElement(Text, null, "of each month")) : serviceBillingModel.isWeekly(billing) || serviceBillingModel.isBiWeekly(billing) ? /*#__PURE__*/React.createElement(React.Fragment, null, "\xA0", /*#__PURE__*/React.createElement(Text, null, "on"), "\xA0", /*#__PURE__*/React.createElement(BillingDayOfWeekField, _extends({}, props, {
    Text: Text
  }))) : null);
  const settings = serviceBillingModel.isOngoing(billing) ? renderOngoingBillingSettings(billing) : serviceBillingModel.isRepeatableManual(billing) ? renderRepeatableManualSettings(billing) : null;
  return settings ? /*#__PURE__*/React.createElement(ConditionalWrapper, {
    condition: showBillingDayOfMonthError,
    wrapper: children => /*#__PURE__*/React.createElement(Tooltip, {
      placement: "right",
      title: /*#__PURE__*/React.createElement(Typography, {
        skin: "invertedStandard"
      }, "It is not possible to bill upfront on the last day of the month. Please change the billing day.")
    }, children)
  }, settings) : null;
}
export function renderBillingSettingsSection(props) {
  const {
    billing
  } = props.service;
  if (serviceBillingModel.isOngoing(billing) || serviceBillingModel.isRepeatableManual(billing) && props.shouldRenderBillingBullet && !(serviceBillingModel.isYearly(billing) || serviceBillingModel.isQuarterly(billing))) {
    return /*#__PURE__*/React.createElement(BillingSettingsSection, props);
  } else {
    return null;
  }
}